import { config } from '../config';
import { headers, APIResponse, stleaf, fetchAPI, SpanTag } from '../helpers';

export const createVolumetricCalculation = (
  st: SpanTag,
  annotationId: string,
  surfaceDataId: string,
  calculationMethod: string,
  fixedElevation?: number | null,
  baseDemId?: string | null,
): Promise<APIResponse> => {
  const url = new URL(
    `${config.API_URL}/v1/volumetrics/create-volume-calculation`,
  );

  const requestBody = JSON.stringify({
    annotationId,
    surfaceDataId,
    calculationMethod,
    fixedElevation,
    baseDemId,
  });

  return fetchAPI(stleaf(st, 'eqc8lo'), url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: requestBody,
  });
};

export const batchVolumeCalculation = (
  st: SpanTag,
  annotationIds: string[],
  surfaceDataId: string,
  calculationMethod: string,
  baseDemId: string | null,
  fixedElevation: number | null,
): Promise<APIResponse> => {
  const url = new URL(
    `${config.API_URL}/v1/volumetrics/batch-volume-calculation`,
  );

  const requestBody = JSON.stringify({
    annotationIds,
    surfaceDataId,
    fixedElevation,
    baseDemId,
    calculationMethod,
  });

  return fetchAPI(stleaf(st, 'suy37s'), url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: requestBody,
  });
};

/** A row from `material_densities`: a global preset, or one the org created. */
export interface VolumetricMaterialOption {
  id: string;
  label: string;
  /** Tonnes per cubic metre — always. Imperial maps convert for display. */
  density: number;
  group: string;
  /** null for Birdi's global presets, set for the org's own materials. */
  organisationId: string | null;
}

/**
 * The material picker's options: global presets plus the org's own materials,
 * already ordered for display.
 */
export const getVolumetricMaterials = (st: SpanTag): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v1/volumetrics/materials`);

  return fetchAPI(stleaf(st, 'mtl9qa'), url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/**
 * Create a material for the current organisation.
 *
 * `density` must already be in t/m³ — convert an imperial entry before
 * calling, because the stored unit never depends on a map setting.
 *
 * Submitting a name the org already has returns that existing material rather
 * than creating a duplicate or changing its density.
 */
export const createVolumetricMaterial = (
  st: SpanTag,
  label: string,
  density: number,
): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v1/volumetrics/materials`);

  return fetchAPI(stleaf(st, 'mtlc7r'), url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ label, density }),
  });
};
